import { createSlice } from '@reduxjs/toolkit';

/**
 * Alert Rules slice (Alerts & Operations P4a — My Alerts). Holds the current user's PERSONAL
 * alert rules per org and the CRUD lifecycle flags. Mutations are saga-backed (SuperFetch to
 * /alerts/rules); the reducer adopts the server's returned rule so the list stays authoritative.
 * Keyed by orgID (a user can switch orgs). Feature-gated on `personalAlerts` at the UI level.
 */
const initialState = {
	// byOrg[orgID] = { items: AlertRule[], loading, saving, loaded }
	byOrg: {},
	error: null
};

const ensure = (state, orgID) => {
	if (!state.byOrg[orgID])
		state.byOrg[orgID] = { items: [], loading: false, saving: false, loaded: false, testing: false };
	return state.byOrg[orgID];
};

const alertRulesSlice = createSlice({
	name: 'alertRules',
	initialState,
	reducers: {
		loadRules: () => {}, // trigger { orgID }
		loadRulesStart(state, action) {
			ensure(state, action.payload.orgID).loading = true;
			state.error = null;
		},
		loadRulesSuccess(state, action) {
			const s = ensure(state, action.payload.orgID);
			s.items = action.payload.rules || [];
			s.loading = false;
			s.loaded = true;
		},
		loadRulesError(state, action) {
			ensure(state, action.payload.orgID).loading = false;
			state.error = action.payload.error;
		},

		saveRule(state, action) {
			ensure(state, action.payload.orgID).saving = true;
		},
		// Adopt a created/updated rule: replace by id if present, else prepend.
		saveRuleSuccess(state, action) {
			const s = ensure(state, action.payload.orgID);
			const rule = action.payload.rule;
			s.saving = false;
			if (!rule) return;
			const i = s.items.findIndex((r) => r.alertRuleID === rule.alertRuleID);
			if (i >= 0) s.items[i] = rule;
			else s.items.unshift(rule);
		},
		saveRuleError(state, action) {
			ensure(state, action.payload.orgID).saving = false;
		},

		deleteRule(state, action) {
			ensure(state, action.payload.orgID).saving = true;
		},
		deleteRuleSuccess(state, action) {
			const s = ensure(state, action.payload.orgID);
			s.saving = false;
			s.items = s.items.filter((r) => r.alertRuleID !== action.payload.alertRuleID);
		},
		deleteRuleError(state, action) {
			ensure(state, action.payload.orgID).saving = false;
		},

		// AO2-4 wizard "Send test notification" (Review step) — a one-off action against a DRAFT
		// rule, no list mutation. `testing` is separate from `saving` so the wizard can show its own
		// spinner without also disabling Create/Save.
		testFire(state, action) {
			ensure(state, action.payload.orgID).testing = true;
		},
		testFireSuccess(state, action) {
			ensure(state, action.payload.orgID).testing = false;
		},
		testFireError(state, action) {
			ensure(state, action.payload.orgID).testing = false;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

export const alertRulesActions = alertRulesSlice.actions;
export default alertRulesSlice.reducer;
